import { Loader2, Camera, Trash2 } from "lucide-react";

export const Section = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-3 mb-5">
      <Icon className="h-5 w-5 text-[hsl(var(--accent))]" />
      <h2 className="display-heading text-2xl">{title}</h2>
    </div>
    <div className="space-y-5">{children}</div>
  </div>
);

export const Grid = ({ children, cols = 2 }) => (
  <div className={`grid sm:grid-cols-${cols} gap-5`}>{children}</div>
);

export const Input = ({ label, value, onChange, testid, placeholder, required, hint, className = "" }) => (
  <div className={className}>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">
      {label}{required ? " *" : ""}
    </label>
    <input
      type="text" value={value || ""} placeholder={placeholder || ""}
      onChange={(e) => onChange(e.target.value)} required={!!required}
      className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
      data-testid={testid}
    />
    {hint && <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">{hint}</p>}
  </div>
);

export const DateInput = ({ label, value, onChange, testid, hint }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">
      {label}
    </label>
    <input
      type="date" value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
      data-testid={testid}
    />
    {hint && <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">{hint}</p>}
  </div>
);

export const Textarea = ({ label, value, onChange, testid, rows = 4, hint }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">{label}</label>
    <textarea
      value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows}
      className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
      data-testid={testid}
    />
    {hint && <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">{hint}</p>}
  </div>
);

export const Select = ({ label, value, onChange, testid, options }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">{label}</label>
    <select
      value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
      data-testid={testid}
    >
      {options.map((o) => <option key={o} value={o}>{o || "— selecionar —"}</option>)}
    </select>
  </div>
);

export const Toggle = ({ label, hint, checked, onChange, testid, disabled }) => (
  <label className={`flex items-start gap-3 cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : ""}`} data-testid={`${testid}-label`}>
    <input
      type="checkbox" checked={!!checked} disabled={!!disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]"
      data-testid={testid}
    />
    <div className="flex-1">
      <div className="text-sm">{label}</div>
      {hint && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{hint}</div>}
    </div>
  </label>
);

export function PhotoUploader({ picture, uploading, fileInputRef, name, onPick, onFileChange, onRemove }) {
  const initials = (name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0] || "").join("").toUpperCase() || "?";
  return (
    <div className="flex items-center gap-6 flex-wrap">
      <div className="relative h-24 w-24 rounded-full overflow-hidden border-2 border-[hsl(var(--border))] bg-[hsl(var(--secondary))] flex-shrink-0">
        {picture ? (
          <img src={picture} alt="Foto de perfil" className="h-full w-full object-cover" data-testid="profile-photo-preview" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-2xl font-medium text-[hsl(var(--primary))] tracking-wider">
            {initials}
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm text-[hsl(var(--foreground))]">
          {picture ? "A sua fotografia aparece no diretório e na sua área pessoal." : "Adicione uma fotografia profissional. Quadrada, mín. 200×200 px, JPG/PNG/WEBP até 5 MB."}
        </p>
        <div className="mt-3 flex gap-2 flex-wrap">
          <button
            type="button" onClick={onPick} disabled={uploading}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[hsl(var(--primary))]/90 disabled:opacity-50"
            data-testid="profile-photo-upload-btn"
          >
            <Camera className="h-3.5 w-3.5" /> {picture ? "Substituir" : "Carregar"} foto
          </button>
          {picture && (
            <button
              type="button" onClick={onRemove} disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-[hsl(var(--border))] text-xs uppercase tracking-[0.18em] hover:border-red-500 hover:text-red-700 disabled:opacity-50"
              data-testid="profile-photo-remove-btn"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remover
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onFileChange}
            className="hidden"
            data-testid="profile-photo-input"
          />
        </div>
      </div>
    </div>
  );
}
